import { Box, Heading2, Heading3, StackView, Text } from 'localmed-core';
import React, { Fragment, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import sortBy from 'lodash/sortBy';

import { DiButton, Link, RadioGroup } from '../../../shared/components/DiComponents';
import { BackLink } from '../../pages/SchedulingPage/components';
import { IStepProps } from '../../pages/SchedulingPage/components/schedulingWizard';
import { BackButton, ContentWrapper, Wrapper } from '../../pages/SchedulingPage/components/styles';
import ReasonForVisitOption from './ReasonForVisitOption';

interface IProps extends IStepProps {}

export default function ReasonSelectionScreen({ wizard }: IProps) {
  useEffect(() => {
    if (wizard?.trackSchedulingEvent) {
      wizard.trackSchedulingEvent('Reason For Visit Selection', 'ReasonSelectionScreen');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!wizard) return null;

  const {
    reasonsForVisit,
    reasonForVisit,
    office,
    patientStatus,
    provider,
    initialSelectedDate,
  } = wizard.values;
  const filteredReasonsForVisit = (reasonsForVisit || []).filter(
    (reason) => reason.patientStatus === patientStatus?.id
  );
  const display = provider?.display || office?.display;

  return (
    <Wrapper>
      <BackLink
        onClick={() => {
          wizard.gotoStep(wizard.step - 1);
        }}
      />
      <ContentWrapper>
        <Box textAlign={{ _: 'center', lg: 'left' }} width="100%">
          <StackView spacing="md" width="100%">
            {filteredReasonsForVisit && filteredReasonsForVisit.length > 0 ? (
              <Fragment>
                <Heading2>
                  <FormattedMessage
                    id="scheduling.ReasonSelectionScreen.WelcomeMessage"
                    defaultMessage="We are excited to {patientStatus, select, NEW {meet you} RETURNING {see you again}}."
                    values={{
                      patientStatus: patientStatus?.id || 'NEW',
                    }}
                  />
                </Heading2>
                <Text>
                  <FormattedMessage
                    id="scheduling.ReasonSelectionScreen.selectReasonMessage"
                    defaultMessage="What would you like to come in for?"
                  />
                </Text>
              </Fragment>
            ) : (
              <Fragment>
                <Heading3>
                  <FormattedMessage
                    id="scheduling.ReasonSelectionScreen.notAcceptingPatientStatusHeader"
                    defaultMessage="Please call us at {phoneNumber} to schedule your appointment."
                    values={{
                      phoneNumber: (
                        <Link
                          href={`tel:${office?.phone}`}
                          className="text-nowrap"
                          fontSize="lg"
                          title="phone number"
                        >
                          {office?.phone}
                        </Link>
                      ),
                    }}
                  />
                </Heading3>
                <Text>
                  <FormattedMessage
                    id="scheduling.ReasonSelectionScreen.notAcceptingPatientStatusBody"
                    defaultMessage="{hasDisplay, select, true {{messageDisplay} is} false {We are}} not accepting {patientStatus, select, NEW {new} RETURNING {returning}} patients online at the moment."
                    values={{
                      hasDisplay: !!display,
                      messageDisplay: display,
                      patientStatus: patientStatus?.id ?? 'NEW',
                    }}
                  />
                </Text>
              </Fragment>
            )}
            <RadioGroup
              name="reasonsForVisit"
              items={sortBy(filteredReasonsForVisit, (r) => r.display.toLowerCase()) || []}
              itemDisplayComponent={ReasonForVisitOption}
              value={reasonForVisit}
              onChange={(value) => {
                wizard.form.batch(() => {
                  wizard.form.change('reasonForVisit', value);
                  wizard.form.change('defaultReasonForVisit', null);
                  wizard.form.change('selectedDate', initialSelectedDate);
                });
              }}
              columns={2}
            />
          </StackView>
        </Box>
      </ContentWrapper>
      <Box display="flex" justifyContent={{ _: 'center', lg: 'space-between' }}>
        <BackButton
          onClick={() => {
            wizard.gotoStep(wizard.step - 1);
          }}
          display={{ _: 'none', lg: 'inherit' }}
          variant="subtle"
          size="xl"
        >
          <FormattedMessage
            id="scheduling.PatientInfoFormScreen.cancelButton"
            defaultMessage="Back"
          />
        </BackButton>
        <DiButton
          onClick={() => {
            wizard.gotoStep(wizard.step + 1);
          }}
          disabled={!reasonForVisit}
          size="xl"
        >
          <FormattedMessage
            id="scheduling.ReasonSelectionScreen.NextButton"
            defaultMessage="Next"
          />
        </DiButton>
      </Box>
    </Wrapper>
  );
}

ReasonSelectionScreen.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  id: 'REASON_SELECTION_SCREEN',
};
